import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { fmtINR, fmtDate, today } from "@/lib/format";
import { Plus, Send, Zap, Car, User, Package, Trash2, CheckCircle2, ArrowRight, ShieldCheck, XCircle, MessageCircle, Mail, FileText, RefreshCw } from "lucide-react";

// -------- Phase 2 tabs (extracted) --------
import FinanceTab from "./dealership/FinanceTab";
import InsuranceTab from "./dealership/InsuranceTab";
import RTOTab from "./dealership/RTOTab";
import JobCardsTab from "./dealership/JobCardsTab";
import DealerConfigPanel from "./dealership/DealerConfigPanel";
import { BulkUploadPanel, ExtendedWarrantyPanel } from "./dealership/MastersExtras";
import DiscountApprovalsTab from "./dealership/DiscountApprovalsTab";
// Iter-148 · Phase-1 · Enhanced Leads UX + configurable master lists
import LeadFormEnhanced from "./dealership/LeadFormEnhanced";
import MastersConfigurableLists from "./dealership/MastersConfigurableLists";
// Iter-151 · Price List (ex-showroom / accessories / extended warranty)
import PriceListsTab from "./dealership/PriceListsTab";
// Iter-153 · Cancellations, Service Desk, Feature Toggles
import CancellationsTab from "./dealership/CancellationsTab";
import ServiceDeskTab from "./dealership/ServiceDeskTab";
import FeatureTogglesPanel from "./dealership/FeatureTogglesPanel";
// Iter-161 · Vehicle 360 lifecycle timeline
import VehicleLifecycleTab from "./dealership/VehicleLifecycleTab";
// Iter-162 · Generic clickable row drilldown
import RowDetailSheet from "@/components/RowDetailSheet";

// -------- shared helpers --------
const STATUS_COLOR = {
  new: "text-blue-500", contacted: "text-cyan-500",
  test_drive_scheduled: "text-purple-500", test_drive_done: "text-purple-600",
  quotation_sent: "text-amber-500", negotiation: "text-orange-500",
  converted: "text-emerald-600", lost: "text-red-500", junk: "text-muted-foreground",
  draft: "text-muted-foreground", sent: "text-blue-500", cancelled: "text-red-500",
  expired: "text-orange-500", booked: "text-blue-500", delivered: "text-emerald-600",
  in_transit: "text-amber-500", in_stock: "text-emerald-600", allocated: "text-purple-600",
  sold: "text-muted-foreground", demo: "text-cyan-500",
  waiting: "text-amber-500", arrived: "text-emerald-600",
  scheduled: "text-blue-500", pending: "text-amber-500",
};

const SOURCES = ["walk_in", "website", "phone", "referral", "social_media", "expo", "other"];
const LEAD_STATUSES = ["new", "contacted", "test_drive_scheduled", "test_drive_done",
                        "quotation_sent", "negotiation", "converted", "lost", "junk"];
const DISCOUNT_CATS = [
  { key: "doctor", label: "Doctor" }, { key: "lawyer", label: "Lawyer" },
  { key: "rural", label: "Rural" }, { key: "loyalty", label: "Loyalty" },
];

// ========== Dashboard KPI Tab ==========
function DashboardTab() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dealership/dashboard").then((r) => setD(r.data)); }, []);
  if (!d) return <div className="text-muted-foreground p-6">Loading…</div>;

  const funnel = [
    { label: "Leads", value: d.counts.leads, testId: "kpi-leads" },
    { label: "Quotations", value: d.counts.quotations, testId: "kpi-quotes" },
    { label: "Bookings", value: d.counts.bookings, testId: "kpi-bookings" },
    { label: "Delivered", value: d.counts.delivered, testId: "kpi-delivered" },
  ];

  return (
    <div className="space-y-4" data-testid="dealer-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {funnel.map((k, i) => (
          <div key={k.label} className="card-sharp p-4" data-testid={k.testId}>
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{k.label}</div>
            <div className="font-heading text-2xl mt-1 tabular-nums">{k.value}</div>
            {i > 0 && funnel[i - 1].value > 0 && (
              <div className="text-[10px] text-muted-foreground mt-1">
                → {Math.round(k.value / funnel[i - 1].value * 100)}% of {funnel[i - 1].label.toLowerCase()}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="card-sharp p-4">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Revenue (booked)</div>
          <div className="font-heading text-2xl mt-1 tabular-nums" data-testid="kpi-revenue">{fmtINR(d.revenue_inr)}</div>
        </div>
        <div className="card-sharp p-4">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Finance penetration</div>
          <div className="font-heading text-2xl mt-1 tabular-nums">{d.finance_penetration_pct}%</div>
        </div>
        <div className="card-sharp p-4">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Insurance attach</div>
          <div className="font-heading text-2xl mt-1 tabular-nums">{d.insurance_attachment_pct}%</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="card-sharp p-4">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-3">Stock ageing (days)</div>
          {Object.entries(d.stock_ageing).map(([bucket, count]) => (
            <div key={bucket} className="flex items-center gap-3 mb-2">
              <div className="w-14 text-xs font-mono text-muted-foreground">{bucket}</div>
              <div className="flex-1 h-6 bg-muted/30 rounded-sm overflow-hidden">
                <div className={`h-full ${bucket === "90+" ? "bg-red-500" : bucket === "61-90" ? "bg-orange-500" : bucket === "31-60" ? "bg-amber-500" : "bg-emerald-500"}`}
                     style={{ width: `${Math.min(100, count * 12)}%` }} />
              </div>
              <div className="w-8 text-right text-xs tabular-nums">{count}</div>
            </div>
          ))}
        </div>
        <div className="card-sharp p-4">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-3">Inventory position</div>
          <div className="grid grid-cols-3 gap-3">
            <div><div className="text-2xl font-heading tabular-nums">{d.counts.in_stock}</div><div className="text-[10px] text-muted-foreground">In stock</div></div>
            <div><div className="text-2xl font-heading tabular-nums">{d.counts.allocated}</div><div className="text-[10px] text-muted-foreground">Allocated</div></div>
            <div><div className="text-2xl font-heading tabular-nums">{d.counts.waitlist}</div><div className="text-[10px] text-muted-foreground">Waitlist</div></div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ========== Leads Tab ==========
function LeadsTab() {
  const [rows, setRows] = useState([]);
  const [models, setModels] = useState([]);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [convertOpen, setConvertOpen] = useState(null); // lead being converted
  const [convertForm, setConvertForm] = useState({ vehicle_model_id: "", accessories: [], discount_categories: [], insurance: 0, extended_warranty: 0 });
  // Iter-152 · effective-date preview
  const [priceMeta, setPriceMeta] = useState(null);

  const load = async () => {
    setRows((await api.get("/dealership/leads")).data);
    setModels((await api.get("/dealership/vehicle-models")).data);
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (lid, status) => {
    try { await api.patch(`/dealership/leads/${lid}`, { status }); toast.success("Updated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const openConvert = (lead) => {
    setConvertOpen(lead);
    setConvertForm({ vehicle_model_id: lead.vehicle_model_id || "", accessories: [], discount_categories: [], insurance: 0, extended_warranty: 0 });
  };
  const runConvert = async () => {
    if (!convertForm.vehicle_model_id) return toast.error("Pick a vehicle model");
    try {
      const r = await api.post(`/dealership/leads/${convertOpen.id}/convert-quotation`, convertForm);
      const q = r.data;
      const eff = q.price_effective_from
        ? ` · price eff. ${new Date(q.price_effective_from).toLocaleDateString("en-IN", {day:"2-digit",month:"short",year:"numeric"})}` +
          (q.price_delta_from_previous ? ` (${q.price_delta_from_previous > 0 ? "+" : ""}${fmtINR(q.price_delta_from_previous)} vs prev)` : "")
        : "";
      toast.success(`Quotation ${q.quotation_number} · On-road ${fmtINR(q.on_road_price)}${eff}`, { duration: 8000 });
      setConvertOpen(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // Iter-152 · Live effective-date preview inside the convert sheet
  useEffect(() => {
    if (!convertOpen || !convertForm.vehicle_model_id) { setPriceMeta(null); return; }
    api.post(`/dealership/pricing/preview`, convertForm)
      .then((r) => setPriceMeta({
        effective_from: r.data.price_effective_from,
        delta: r.data.price_delta_from_previous,
        prev_ex: r.data.previous_ex_showroom,
        ex: r.data.ex_showroom,
        notes: r.data.price_notes,
        source: r.data.price_source,
      }))
      .catch(() => setPriceMeta(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convertOpen?.id, convertForm.vehicle_model_id, convertForm.insurance, convertForm.extended_warranty, JSON.stringify(convertForm.discount_categories)]);

  const modelName = (id) => {
    const m = models.find((x) => x.id === id);
    return m ? `${m.name} · ${m.variant}` : "—";
  };

  return (
    <div className="space-y-3" data-testid="leads-tab">
      <div className="flex justify-end">
        <Button onClick={() => setOpen(true)} className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="new-lead-btn">
          <Plus className="w-3.5 h-3.5" /> New Lead
        </Button>
      </div>
      <div className="card-sharp overflow-hidden">
        <table className="w-full dense">
          <thead><tr><th>Lead#</th><th>Customer</th><th>Mobile</th><th>Vehicle</th><th>Category</th><th>Status</th><th>Assigned</th><th></th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="8" className="text-center py-10 text-muted-foreground">No leads yet. Create your first lead.</td></tr>}
            {rows.map((l) => (
              <tr key={l.id} data-testid={`lead-row-${l.lead_number}`}>
                <td className="font-mono text-xs">{l.lead_number}</td>
                <td className="text-xs"><User className="inline w-3 h-3 mr-1 text-muted-foreground" />{l.first_name} {l.last_name || ""}</td>
                <td className="font-mono text-xs">{l.mobile}</td>
                <td className="text-xs">{modelName(l.vehicle_model_id)}</td>
                <td className="text-[10px] font-mono uppercase text-muted-foreground">{l.purchase_category || l.source}</td>
                <td>
                  <Select value={l.status} onValueChange={(v) => updateStatus(l.id, v)}>
                    <SelectTrigger className={`rounded-sm h-7 text-[10px] font-mono uppercase w-40 ${STATUS_COLOR[l.status] || ""}`} data-testid={`lead-status-${l.lead_number}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>{LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="text-[10px] font-mono text-muted-foreground">{(l.assigned_to || "").slice(0, 8)}</td>
                <td className="text-right">
                  {!l.converted_quotation_id && ["new", "contacted", "test_drive_done", "negotiation"].includes(l.status) && (
                    <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-accent" onClick={() => openConvert(l)} data-testid={`convert-lead-${l.lead_number}`}>
                      <Zap className="w-3 h-3" /> → Quote
                    </Button>
                  )}
                  {l.converted_quotation_id && <span className="text-[10px] font-mono uppercase text-emerald-600">✓ Quoted</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Iter-148 · Enhanced New Lead sheet with configurable masters, exchange info, DL OCR, finance & insurance */}
      <LeadFormEnhanced open={open} onClose={() => setOpen(false)} onCreated={() => { setOpen(false); load(); }} />
      <RowDetailSheet row={detail} onClose={() => setDetail(null)} title="Lead" testId="lead-detail" />

      {/* Convert lead → quotation sheet */}
      <Sheet open={!!convertOpen} onOpenChange={(v) => !v && setConvertOpen(null)}>
        <SheetContent side="right" className="w-full sm:max-w-xl rounded-none space-y-3 overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="font-heading">Lead → Quotation</SheetTitle>
            <SheetDescription className="sr-only">Generate a full on-road quotation for this lead.</SheetDescription>
          </SheetHeader>
          {convertOpen && <>
            <div className="border rounded-sm p-3 bg-muted/30 text-xs">
              <div><b>{convertOpen.first_name} {convertOpen.last_name}</b> · <span className="font-mono">{convertOpen.mobile}</span></div>
              {convertOpen.notes && <div className="text-muted-foreground mt-1">{convertOpen.notes}</div>}
            </div>
            <div>
              <Label>Vehicle model *</Label>
              <Select value={convertForm.vehicle_model_id} onValueChange={(v) => setConvertForm({ ...convertForm, vehicle_model_id: v })}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="convert-vehicle"><SelectValue placeholder="Pick model" /></SelectTrigger>
                <SelectContent>{models.map((m) => <SelectItem key={m.id} value={m.id}>{m.name} · {m.variant} · {fmtINR(m.ex_showroom_price)}</SelectItem>)}</SelectContent>
              </Select>
              {/* Iter-152 · Effective-date banner appears when a Price-List row exists */}
              {priceMeta?.effective_from && (
                <div className="mt-2 text-[11px] px-2.5 py-1.5 rounded-sm bg-emerald-50 border border-emerald-200 text-emerald-900 flex items-center gap-2" data-testid="convert-price-eff">
                  <span className="text-[9px] font-mono uppercase bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded-sm">Price List</span>
                  Price effective <b>{new Date(priceMeta.effective_from).toLocaleDateString("en-IN", {day:"2-digit",month:"short",year:"numeric"})}</b> · <b>{fmtINR(priceMeta.ex)}</b>
                  {priceMeta.delta !== 0 && priceMeta.prev_ex ? (
                    <span className={priceMeta.delta > 0 ? "text-red-700" : "text-emerald-700"}>
                      · {priceMeta.delta > 0 ? "up" : "down"} {fmtINR(Math.abs(priceMeta.delta))} from prev ({fmtINR(priceMeta.prev_ex)})
                    </span>
                  ) : null}
                  {priceMeta.notes ? <span className="italic text-emerald-700 truncate">· {priceMeta.notes}</span> : null}
                </div>
              )}
            </div>
            <div>
              <Label>Discount categories (each 2.5%, cap 5%)</Label>
              <div className="flex gap-2 mt-1 flex-wrap">
                {DISCOUNT_CATS.map((d) => {
                  const on = convertForm.discount_categories.includes(d.key);
                  return (
                    <button key={d.key} type="button"
                            onClick={() => setConvertForm({
                              ...convertForm,
                              discount_categories: on ? convertForm.discount_categories.filter((x) => x !== d.key)
                                                        : [...convertForm.discount_categories, d.key],
                            })}
                            className={`text-[10px] font-mono uppercase px-2 py-1 border rounded-sm ${on ? "bg-accent text-white border-accent" : "bg-background"}`}
                            data-testid={`disc-${d.key}`}>
                      {d.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Insurance (est.)</Label><Input type="number" value={convertForm.insurance} onChange={(e) => setConvertForm({ ...convertForm, insurance: Number(e.target.value) })} className="rounded-sm mt-1" data-testid="convert-insurance" /></div>
              <div><Label>Extended warranty</Label><Input type="number" value={convertForm.extended_warranty} onChange={(e) => setConvertForm({ ...convertForm, extended_warranty: Number(e.target.value) })} className="rounded-sm mt-1" /></div>
            </div>
            <Button onClick={runConvert} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white gap-1" data-testid="convert-run">
              <Zap className="w-3.5 h-3.5" /> Generate Quotation
            </Button>
          </>}
        </SheetContent>
      </Sheet>
    </div>
  );
}

// ========== Quotations Tab ==========
function QuotationsTab() {
  const [rows, setRows] = useState([]);
  const [models, setModels] = useState([]);
  const [detail, setDetail] = useState(null);
  const [convertOpen, setConvertOpen] = useState(null);
  const [convertForm, setConvertForm] = useState({ payment_mode: "online", payment_type: "self_funded", payment_reference: "", expected_delivery_date: "" });

  const load = async () => {
    setRows((await api.get("/dealership/quotations")).data);
    setModels((await api.get("/dealership/vehicle-models")).data);
  };
  useEffect(() => { load(); }, []);

  const modelName = (id) => {
    const m = models.find((x) => x.id === id);
    return m ? `${m.name} · ${m.variant}` : "—";
  };
  const send = async (q) => {
    try { await api.post(`/dealership/quotations/${q.id}/send`); toast.success("Quotation sent"); load(); }
    catch (e) { toast.error("Failed"); }
  };
  // Iter-149 · Phase 2 · WhatsApp + Email share via user's own device (deep links)
  const shareWA = async (q) => {
    try {
      const r = await api.post(`/dealership/quotations/${q.id}/share/whatsapp`);
      window.open(r.data.url, "_blank");
      toast.success("Opening WhatsApp…");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const shareEmail = async (q) => {
    try {
      const r = await api.post(`/dealership/quotations/${q.id}/share/email`);
      window.open(r.data.url, "_blank");
      toast.success("Opening email client…");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const openConvert = (q) => { setConvertOpen(q); setConvertForm({ payment_mode: "online", payment_type: "self_funded", payment_reference: "", expected_delivery_date: "" }); };
  const runConvert = async () => {
    try {
      const r = await api.post(`/dealership/bookings/from-quotation/${convertOpen.id}`, convertForm);
      toast.success(`Booking ${r.data.booking_number} · allocation ${r.data.allocation_status}`);
      setConvertOpen(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-3" data-testid="quotations-tab">
      <div className="card-sharp overflow-hidden">
        <table className="w-full dense">
          <thead><tr><th>Quote#</th><th>Customer</th><th>Vehicle</th><th className="text-right">Ex-Show</th><th className="text-right">On-Road</th><th className="text-right">Booking</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="8" className="text-center py-10 text-muted-foreground">No quotations yet. Convert a lead to quote.</td></tr>}
            {rows.map((q) => (
              <tr key={q.id} data-testid={`quote-row-${q.quotation_number}`}>
                <td className="font-mono text-xs">{q.quotation_number}</td>
                <td className="text-xs">{q.customer?.first_name} {q.customer?.last_name}<div className="text-[10px] text-muted-foreground font-mono">{q.customer?.mobile}</div></td>
                <td className="text-xs">{modelName(q.vehicle_model_id)}</td>
                <td className="text-right tabular-nums text-xs">{fmtINR(q.ex_showroom)}</td>
                <td className="text-right tabular-nums font-medium">{fmtINR(q.on_road_price)}</td>
                <td className="text-right tabular-nums text-xs">{fmtINR(q.booking_amount)}</td>
                <td><span className={`text-[10px] font-mono uppercase ${STATUS_COLOR[q.status] || ""}`}>{q.status}</span></td>
                <td className="text-right">
                  {q.status === "draft" && <Button size="sm" variant="ghost" className="h-7 text-xs gap-1" onClick={() => send(q)} data-testid={`send-quote-${q.quotation_number}`}><Send className="w-3 h-3" /> Send</Button>}
                  {q.status !== "draft" && (
                    <>
                      <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-emerald-700" onClick={() => shareWA(q)} data-testid={`wa-quote-${q.quotation_number}`} title="Share on WhatsApp">
                        <MessageCircle className="w-3 h-3" /> WhatsApp
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-blue-600" onClick={() => shareEmail(q)} data-testid={`email-quote-${q.quotation_number}`} title="Share via Email">
                        <Mail className="w-3 h-3" /> Email
                      </Button>
                    </>
                  )}
                  {["sent", "draft"].includes(q.status) && !q.converted_booking_id && (
                    <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-accent" onClick={() => openConvert(q)} data-testid={`book-quote-${q.quotation_number}`}>
                      <Zap className="w-3 h-3" /> → Book
                    </Button>
                  )}
                  {q.converted_booking_id && <span className="text-[10px] font-mono uppercase text-emerald-600">✓ Booked</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Sheet open={!!convertOpen} onOpenChange={(v) => !v && setConvertOpen(null)}>
        <SheetContent side="right" className="w-full sm:max-w-lg rounded-none space-y-3">
          <SheetHeader>
            <SheetTitle className="font-heading">Quotation → Booking</SheetTitle>
            <SheetDescription className="sr-only">Confirm booking details and collect advance.</SheetDescription>
          </SheetHeader>
          {convertOpen && <>
            <div className="border rounded-sm p-3 bg-muted/30 text-xs space-y-1">
              <div><b>{convertOpen.customer?.first_name} {convertOpen.customer?.last_name}</b></div>
              <div>Vehicle: {modelName(convertOpen.vehicle_model_id)}</div>
              <div>On-Road: <span className="font-medium tabular-nums">{fmtINR(convertOpen.on_road_price)}</span></div>
              <div>Booking amount: <span className="font-medium tabular-nums" data-testid="convert-booking-amount">{fmtINR(convertOpen.booking_amount)}</span></div>
              {convertOpen.booking_amount >= 200000 && <div className="text-red-500 text-[10px] font-mono">⚠ CASH BLOCKED (RBI ₹2L limit) — use online/cheque/DD</div>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Payment mode *</Label>
                <Select value={convertForm.payment_mode} onValueChange={(v) => setConvertForm({ ...convertForm, payment_mode: v })}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="pay-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="online">Online (UPI/Card)</SelectItem>
                    <SelectItem value="cash" disabled={convertOpen.booking_amount >= 200000}>Cash{convertOpen.booking_amount >= 200000 ? " (blocked)" : ""}</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="dd">DD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Payment type</Label>
                <Select value={convertForm.payment_type} onValueChange={(v) => setConvertForm({ ...convertForm, payment_type: v })}>
                  <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="self_funded">Self-funded</SelectItem><SelectItem value="finance">Finance</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Payment reference</Label><Input value={convertForm.payment_reference} onChange={(e) => setConvertForm({ ...convertForm, payment_reference: e.target.value })} className="rounded-sm mt-1 font-mono" placeholder="UPI txn / cheque no." /></div>
              <div className="col-span-2"><Label>Expected delivery</Label><Input type="date" value={convertForm.expected_delivery_date} onChange={(e) => setConvertForm({ ...convertForm, expected_delivery_date: e.target.value })} className="rounded-sm mt-1" /></div>
            </div>
            <Button onClick={runConvert} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white gap-1" data-testid="convert-book-run">
              <Zap className="w-3.5 h-3.5" /> Create Booking
            </Button>
          </>}
        </SheetContent>
      </Sheet>
      <RowDetailSheet row={detail} onClose={() => setDetail(null)} title="Quotation" testId="quote-detail" />
    </div>
  );
}

// ========== Bookings Tab ==========
function BookingsTab() {
  const [rows, setRows] = useState([]);
  const [models, setModels] = useState([]);
  const [detail, setDetail] = useState(null);
  const [gpBooking, setGpBooking] = useState(null);
  const [gpChecks, setGpChecks] = useState(null);
  // Iter-153 · Cancellation lodge state
  const [cancelBooking, setCancelBooking] = useState(null);
  const [cancelForm, setCancelForm] = useState({ reason: "", requester_comment: "" });

  const load = async () => {
    setRows((await api.get("/dealership/bookings")).data);
    setModels((await api.get("/dealership/vehicle-models")).data);
  };
  useEffect(() => { load(); }, []);

  const modelName = (id) => {
    const m = models.find((x) => x.id === id);
    return m ? `${m.name} · ${m.variant}` : "—";
  };
  const openGP = async (b) => {
    setGpBooking(b);
    try { const r = await api.get(`/dealership/bookings/${b.id}/gate-pass-readiness`); setGpChecks(r.data.checks); }
    catch { setGpChecks(null); }
  };
  const createDelivery = async (b) => {
    try {
      const r = await api.post("/dealership/deliveries", { booking_id: b.id, scheduled_at: b.expected_delivery_date || today() });
      toast.success(`Delivery ${r.data.delivery_number} scheduled`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const lodgeCancellation = async () => {
    if (!cancelForm.reason.trim()) return toast.error("Reason is required");
    try {
      const r = await api.post(`/dealership/bookings/${cancelBooking.id}/cancel-request`, cancelForm);
      toast.success(r.data.requires_reporting_authority
        ? "Cancellation raised — sent for reporting-authority approval"
        : "Cancellation raised — awaiting confirmation");
      setCancelBooking(null); setCancelForm({ reason: "", requester_comment: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-3" data-testid="bookings-tab">
      <div className="card-sharp overflow-hidden">
        <table className="w-full dense">
          <thead><tr><th>Booking#</th><th>Customer</th><th>Vehicle</th><th className="text-right">On-Road</th><th className="text-right">Balance</th><th>Payment</th><th>Allocation</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="9" className="text-center py-10 text-muted-foreground">No bookings yet.</td></tr>}
            {rows.map((b) => (
              <tr key={b.id} data-testid={`booking-row-${b.booking_number}`}
                    className="cursor-pointer hover:bg-muted/40 transition-colors"
                    onClick={() => setDetail(b)}>
                <td className="font-mono text-xs">{b.booking_number}</td>
                <td className="text-xs">{b.customer?.first_name} {b.customer?.last_name}</td>
                <td className="text-xs">{modelName(b.vehicle_model_id)}{b.allocated_vin && <div className="text-[9px] font-mono text-emerald-600">VIN: {b.allocated_vin}</div>}</td>
                <td className="text-right tabular-nums font-medium">{fmtINR(b.on_road_price)}</td>
                <td className="text-right tabular-nums">{fmtINR(b.balance_amount)}</td>
                <td><span className={`text-[10px] font-mono uppercase ${STATUS_COLOR[b.overall_payment_status] || ""}`}>{b.overall_payment_status}</span></td>
                <td><span className={`text-[10px] font-mono uppercase ${STATUS_COLOR[b.allocation_status] || ""}`}>{b.allocation_status}</span></td>
                <td><span className={`text-[10px] font-mono uppercase ${STATUS_COLOR[b.booking_status] || ""}`}>{b.booking_status}</span></td>
                <td className="text-right" onClick={(e) => e.stopPropagation()}>
                  {/* Iter-149 · Phase 3 · Payment link + VIN notification */}
                  {b.overall_payment_status !== "fully_paid" && (
                    <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-purple-600" onClick={async () => {
                      try {
                        const r = await api.post(`/dealership/bookings/${b.id}/payment-link`);
                        const url = `${window.location.origin}${r.data.hosted_url}`;
                        await navigator.clipboard.writeText(url).catch(() => {});
                        toast.success(`Payment link copied · ${fmtINR(r.data.amount)}`, { duration: 5000 });
                      } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                    }} data-testid={`pay-link-${b.booking_number}`} title="Generate & copy mock payment link">
                      💳 Pay Link
                    </Button>
                  )}
                  {b.allocated_vin && (
                    <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-emerald-700" onClick={async () => {
                      try {
                        const r = await api.get(`/dealership/bookings/${b.id}/vin-notification`);
                        if (r.data.wa_url) window.open(r.data.wa_url, "_blank");
                        toast.success(`VIN notification prepared · ${r.data.vin}`);
                      } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                    }} data-testid={`vin-notify-${b.booking_number}`} title="WhatsApp VIN/Chassis to customer">
                      <MessageCircle className="w-3 h-3" /> VIN
                    </Button>
                  )}
                  {b.booking_status === "booked" && b.allocation_status === "allocated" && b.delivery_status === "pending" && (
                    <Button size="sm" variant="ghost" className="h-7 text-xs gap-1" onClick={() => createDelivery(b)} data-testid={`schedule-delivery-${b.booking_number}`}>
                      <Car className="w-3 h-3" /> Schedule
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-emerald-700" onClick={() => openGP(b)} data-testid={`gp-check-${b.booking_number}`}>
                    <ShieldCheck className="w-3 h-3" /> Gate Pass
                  </Button>
                  {b.booking_status === "delivered" && !b.invoice_id && (
                    <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-blue-600" onClick={async () => {
                      try { const r = await api.post(`/dealership/bookings/${b.id}/generate-invoice`);
                        if (r.data.already_invoiced) toast.info("Invoice already generated");
                        else toast.success(`Invoice ${r.data.invoice.invoice_number}${r.data.einvoice_required ? " · e-invoice REQUIRED (turnover > threshold)" : ""}`);
                        load();
                      } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                    }} data-testid={`gen-invoice-${b.booking_number}`}>
                      📄 Generate Invoice
                    </Button>
                  )}
                  {b.invoice_id && <span className="text-[10px] font-mono uppercase text-emerald-700 ml-1">✓ invoiced</span>}
                  {b.booking_status !== "cancelled" && b.booking_status !== "delivered" && !b.cancellation_id && (
                    <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-red-600"
                      onClick={() => { setCancelBooking(b); setCancelForm({ reason: "", requester_comment: "" }); }}
                      data-testid={`cancel-lodge-${b.booking_number}`} title="Raise cancellation request">
                      <XCircle className="w-3 h-3" /> Cancel
                    </Button>
                  )}
                  {b.cancellation_status && (
                    <span className={`text-[10px] font-mono uppercase ml-1 ${
                      b.cancellation_status === "approved" ? "text-red-600" :
                      b.cancellation_status === "rejected" ? "text-muted-foreground" :
                      "text-amber-600"
                    }`} data-testid={`cancel-status-${b.booking_number}`}>
                      ⚠ cancel · {b.cancellation_status}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Sheet open={!!gpBooking} onOpenChange={(v) => !v && setGpBooking(null)}>
        <SheetContent side="right" className="w-full sm:max-w-md rounded-none space-y-3">
          <SheetHeader>
            <SheetTitle className="font-heading">Gate Pass Readiness</SheetTitle>
            <SheetDescription className="sr-only">7-point delivery checklist. All must be met before gate pass issuance.</SheetDescription>
          </SheetHeader>
          {gpBooking && gpChecks && (
            <div className="space-y-2" data-testid="gp-checks">
              <div className="text-xs text-muted-foreground">Booking <span className="font-mono">{gpBooking.booking_number}</span></div>
              {Object.entries(gpChecks.labels).map(([k, label]) => (
                <div key={k} className="flex items-center gap-2 text-xs" data-testid={`gp-check-${k}`}>
                  {gpChecks[k] ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <XCircle className="w-4 h-4 text-red-500" />}
                  <span className={gpChecks[k] ? "text-foreground" : "text-muted-foreground"}>{label}</span>
                </div>
              ))}
              <div className="border-t pt-2 mt-2 text-xs">
                {gpChecks.all_met ? (
                  <div className="text-emerald-600 font-mono uppercase">✓ Ready — schedule delivery + issue gate pass</div>
                ) : (
                  <div className="text-amber-600 font-mono uppercase">Pending items — cannot issue gate pass</div>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Iter-153 · Cancellation lodge sheet */}
      <Sheet open={!!cancelBooking} onOpenChange={(v) => !v && setCancelBooking(null)}>
        <SheetContent side="right" className="w-full sm:max-w-md rounded-none space-y-3">
          <SheetHeader>
            <SheetTitle className="font-heading flex items-center gap-1"><XCircle className="w-4 h-4 text-red-500" /> Cancel Booking</SheetTitle>
            <SheetDescription>Raise a cancellation request. Deduction slabs run automatically; post-allocation bookings and beyond-15-day slabs land in the Approvals inbox.</SheetDescription>
          </SheetHeader>
          {cancelBooking && (
            <div className="space-y-3 text-xs">
              <div className="border rounded-sm p-2 bg-slate-50/60">
                <div>Booking <b className="font-mono">{cancelBooking.booking_number}</b></div>
                <div>Customer <b>{cancelBooking.customer?.first_name} {cancelBooking.customer?.last_name}</b></div>
                <div>Amount paid so far <span className="font-mono">{fmtINR(cancelBooking.paid_amount || cancelBooking.booking_amount)}</span></div>
                <div>Allocation <span className="font-mono uppercase">{cancelBooking.allocation_status || "pending"}</span></div>
              </div>
              <div>
                <Label className="text-xs">Reason *</Label>
                <Input value={cancelForm.reason} onChange={(e) => setCancelForm({ ...cancelForm, reason: e.target.value })}
                  placeholder="e.g. Customer opted for another brand" className="rounded-sm mt-1" data-testid="cancel-reason" />
              </div>
              <div>
                <Label className="text-xs">Additional comment</Label>
                <Textarea value={cancelForm.requester_comment}
                  onChange={(e) => setCancelForm({ ...cancelForm, requester_comment: e.target.value })}
                  rows={3} placeholder="Any context helpful for the reviewer…" className="rounded-sm mt-1" data-testid="cancel-comment" />
              </div>
              <Button onClick={lodgeCancellation} className="w-full rounded-sm bg-red-600 hover:bg-red-700 text-white" data-testid="cancel-lodge-save">
                Raise Cancellation Request
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

// ========== Stock Tab (Iter-151 · Read-only · reports + analytics) ==========
// Adding vehicle stock now happens via /app/inventory (Item type = Vehicle).
// This tab is a live view + analytics dashboard.
function StockTab() {
  const [rows, setRows] = useState([]);
  const [models, setModels] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const [s, m, a] = await Promise.all([
        api.get("/dealership/stock"),
        api.get("/dealership/vehicle-models"),
        api.get("/dealership/stock/analytics"),
      ]);
      setRows(s.data); setModels(m.data); setAnalytics(a.data);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const modelName = (id) => {
    const m = models.find((x) => x.id === id);
    return m ? `${m.name} · ${m.variant}` : "—";
  };
  const doPDI = async (s) => {
    try { await api.post(`/dealership/stock/${s.id}/pdi-done`); toast.success("PDI marked done"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="space-y-3" data-testid="stock-tab">
      <div className="border border-dashed border-sky-300 bg-sky-50/40 rounded-sm p-3 text-xs flex items-start gap-2" data-testid="stock-banner">
        <Package className="w-4 h-4 text-sky-700 shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="font-medium text-sky-900">Vehicle Stock is now unified with Inventory</div>
          <div className="text-sky-800 text-[11px] mt-0.5">
            Add or edit vehicles from <b>Inventory → Items</b> with item type <b>🚗 Vehicle</b>. All VIN / Chassis / Engine / YoM / Ex-showroom fields live on the Item — one source of truth. This tab is now read-only with reports & PDI operations.
          </div>
        </div>
        {/* Iter-152 · One-off admin migration button */}
        <Button size="sm" variant="ghost"
          onClick={async () => {
            if (!window.confirm("Migrate all legacy VIN stock rows into Items with type=vehicle? This is safe & idempotent — rows already migrated will be skipped.")) return;
            try {
              const r = await api.post("/dealership/stock/migrate-to-items");
              const s = r.data;
              toast.success(`Migration done · scanned ${s.scanned} · created ${s.created} · skipped ${s.already_present}${s.errors?.length ? ` · ${s.errors.length} errors` : ""}`, { duration: 9000 });
              if (s.errors?.length) console.warn("Stock migration errors:", s.errors);
              load();
            } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
          }}
          className="rounded-sm h-7 text-[10px] font-mono uppercase text-sky-800 border border-sky-300 hover:bg-sky-100"
          data-testid="stock-migrate-btn">
          ↻ Migrate legacy stock
        </Button>
        <a href="/app/inventory" className="text-[10px] font-mono uppercase text-accent hover:underline whitespace-nowrap ml-1" data-testid="stock-goto-inventory">
          → Inventory
        </a>
      </div>

      {/* Analytics cards */}
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="stock-analytics">
          <div className="border rounded-sm p-3 bg-background">
            <div className="text-[10px] font-mono uppercase text-muted-foreground">Total units</div>
            <div className="text-2xl font-bold tabular-nums">{analytics.total_units}</div>
          </div>
          <div className="border rounded-sm p-3 bg-background">
            <div className="text-[10px] font-mono uppercase text-muted-foreground">Inventory value</div>
            <div className="text-2xl font-bold tabular-nums text-blue-800">{fmtINR(analytics.inventory_value)}</div>
          </div>
          <div className="border rounded-sm p-3 bg-background">
            <div className="text-[10px] font-mono uppercase text-muted-foreground">Available / Allocated / Sold</div>
            <div className="text-sm font-medium mt-1">
              <span className="text-emerald-700 tabular-nums">{analytics.allocation.in_stock || 0}</span> ·
              <span className="text-amber-700 tabular-nums"> {analytics.allocation.allocated || 0}</span> ·
              <span className="text-slate-500 tabular-nums"> {analytics.allocation.sold || 0}</span>
            </div>
          </div>
          <div className="border rounded-sm p-3 bg-background">
            <div className="text-[10px] font-mono uppercase text-muted-foreground">Ageing bands</div>
            <div className="text-[11px] font-mono mt-1">
              <div>0-30d: <b className="tabular-nums">{analytics.aging["0_30"]}</b></div>
              <div>30-60d: <b className="tabular-nums">{analytics.aging["30_60"]}</b></div>
              <div className="text-orange-600">60-90d: <b className="tabular-nums">{analytics.aging["60_90"]}</b></div>
              <div className="text-red-600">90+ d: <b className="tabular-nums">{analytics.aging["90_plus"]}</b></div>
            </div>
          </div>
        </div>
      )}

      {/* Waitlist depth (unallocated demand per model) */}
      {analytics?.waitlist_by_model?.length > 0 && (
        <div className="border rounded-sm overflow-hidden" data-testid="stock-waitlist-depth">
          <div className="bg-muted/30 px-3 py-1.5 text-[10px] font-mono uppercase text-muted-foreground">Waitlist depth · unallocated demand per model</div>
          <table className="w-full text-xs">
            <thead className="bg-muted/10 text-[10px]"><tr><th className="text-left px-3 py-1.5">Model</th><th className="text-right px-3 py-1.5">Bookings waiting</th></tr></thead>
            <tbody>
              {analytics.waitlist_by_model.map((w) => (
                <tr key={w.model_id} className="border-t"><td className="px-3 py-1.5">{w.model}</td><td className="px-3 py-1.5 text-right tabular-nums font-medium">{w.count}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Stock table (read-only apart from PDI operations) */}
      <div className="card-sharp overflow-hidden">
        <table className="w-full dense">
          <thead><tr><th>VIN</th><th>Vehicle</th><th>Colour</th><th className="text-right">Age (d)</th><th>PDI</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="7" className="text-center py-10 text-muted-foreground">No vehicles in stock. Add via Inventory → Items → Type: 🚗 Vehicle.</td></tr>}
            {rows.map((s) => (
              <tr key={s.id} data-testid={`stock-row-${s.vin}`}>
                <td className="font-mono text-[10px]">{s.vin}</td>
                <td className="text-xs">{modelName(s.vehicle_model_id)}</td>
                <td className="text-xs">{s.colour}</td>
                <td className={`text-right tabular-nums text-xs ${s.age_days > 90 ? "text-red-500 font-medium" : s.age_days > 60 ? "text-orange-500" : ""}`}>{s.age_days}</td>
                <td><span className={`text-[10px] font-mono uppercase ${s.pdi_status === "done" ? "text-emerald-600" : "text-muted-foreground"}`}>{s.pdi_status}</span></td>
                <td><span className={`text-[10px] font-mono uppercase ${STATUS_COLOR[s.status] || ""}`}>{s.status}</span></td>
                <td className="text-right">
                  {s.pdi_status !== "done" && s.status !== "sold" && (
                    <Button size="sm" variant="ghost" className="h-7 text-xs gap-1" onClick={() => doPDI(s)} data-testid={`pdi-${s.vin}`}><CheckCircle2 className="w-3 h-3" /> Mark PDI</Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ========== Waitlist Tab ==========
function WaitlistTab() {
  const [rows, setRows] = useState([]);
  const [models, setModels] = useState([]);
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    api.get("/dealership/waitlist").then((r) => setRows(r.data));
    api.get("/dealership/vehicle-models").then((r) => setModels(r.data));
  }, []);
  const modelName = (id) => {
    const m = models.find((x) => x.id === id);
    return m ? `${m.name} · ${m.variant}` : "—";
  };
  return (
    <div className="card-sharp overflow-hidden" data-testid="waitlist-tab">
      <table className="w-full dense">
        <thead><tr><th>WL#</th><th className="text-right">Priority</th><th>Customer</th><th>Vehicle</th><th>Colour</th><th>Waitlist Date</th><th>Status</th></tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan="7" className="text-center py-10 text-muted-foreground">No pending waitlist entries.</td></tr>}
          {rows.map((w) => (
            <tr key={w.id} data-testid={`wl-row-${w.waitlist_number}`}
                  className="cursor-pointer hover:bg-muted/40 transition-colors"
                  onClick={() => setDetail(w)}>
              <td className="font-mono text-xs">{w.waitlist_number}</td>
              <td className="text-right tabular-nums text-xs font-medium">#{w.priority}</td>
              <td className="text-xs">{w.customer?.first_name} {w.customer?.last_name}</td>
              <td className="text-xs">{modelName(w.vehicle_model_id)}</td>
              <td className="text-xs">{w.colour || "—"}</td>
              <td className="text-xs">{fmtDate(w.waitlist_date)}</td>
              <td><span className={`text-[10px] font-mono uppercase ${STATUS_COLOR[w.status] || ""}`}>{w.status}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
      <RowDetailSheet row={detail} onClose={() => setDetail(null)} title="Waitlist entry" testId="waitlist-detail" />
    </div>
  );
}

// ========== Deliveries Tab ==========
function DeliveriesTab() {
  const [rows, setRows] = useState([]);
  const [bookings, setBookings] = useState({});
  const [detail, setDetail] = useState(null);
  // Iter-149 · Phase 4 · eSign + Security OTP state
  const [esignOpen, setEsignOpen] = useState(null);   // delivery being signed
  const [esignForm, setEsignForm] = useState({ customer_name: "", aadhaar_last4: "" });
  const [otpBusy, setOtpBusy] = useState(null);
  const [otpVal, setOtpVal] = useState("");
  const [otpTarget, setOtpTarget] = useState(null);

  const load = async () => {
    const [d, b] = await Promise.all([api.get("/dealership/deliveries"), api.get("/dealership/bookings")]);
    setRows(d.data);
    setBookings(Object.fromEntries(b.data.map((x) => [x.id, x])));
  };
  useEffect(() => { load(); }, []);

  const issue = async (dv) => {
    try {
      const r = await api.post(`/dealership/deliveries/${dv.id}/issue-gate-pass`);
      toast.success(`Gate pass issued: ${r.data.gate_pass_number}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const saveEsign = async () => {
    if (!esignForm.customer_name.trim()) return toast.error("Customer name required");
    if (!/^\d{4}$/.test(esignForm.aadhaar_last4)) return toast.error("Aadhaar last-4 must be 4 digits");
    try {
      await api.post(`/dealership/deliveries/${esignOpen.id}/esign`, esignForm);
      toast.success("eSign recorded — you can now generate exit OTP.");
      setEsignOpen(null); setEsignForm({ customer_name: "", aadhaar_last4: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const genOtp = async (dv) => {
    setOtpBusy(dv.id);
    try {
      const r = await api.post(`/dealership/deliveries/${dv.id}/security-otp/generate`);
      toast.success(`OTP: ${r.data.otp} — share with customer / security. Expires in 30 min.`, { duration: 15000 });
      setOtpTarget(dv);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setOtpBusy(null); }
  };

  const verifyOtp = async () => {
    if (!/^\d{6}$/.test(otpVal)) return toast.error("OTP must be 6 digits");
    try {
      await api.post(`/dealership/deliveries/${otpTarget.id}/security-otp/verify`, { otp: otpVal });
      toast.success("Vehicle release confirmed — gate cleared for exit.");
      setOtpTarget(null); setOtpVal("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-3" data-testid="deliveries-tab">
      <div className="card-sharp overflow-hidden">
        <table className="w-full dense">
          <thead><tr><th>Delivery#</th><th>Booking</th><th>Customer</th><th>Scheduled</th><th>Status</th><th>eSign</th><th>Gate Pass</th><th>Exit OTP</th><th></th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="9" className="text-center py-10 text-muted-foreground">No deliveries scheduled.</td></tr>}
            {rows.map((d) => {
              const b = bookings[d.booking_id];
              const hasSign = !!d.esign_ack;
              const otp = d.security_otp || {};
              return (
                <tr key={d.id} data-testid={`delivery-row-${d.delivery_number}`}>
                  <td className="font-mono text-xs">{d.delivery_number}</td>
                  <td className="font-mono text-xs">{b?.booking_number || d.booking_id.slice(0, 8)}</td>
                  <td className="text-xs">{b?.customer?.first_name} {b?.customer?.last_name}</td>
                  <td className="text-xs">{fmtDate(d.scheduled_at)}</td>
                  <td><span className={`text-[10px] font-mono uppercase ${STATUS_COLOR[d.status] || ""}`}>{d.status}</span></td>
                  <td className="text-xs">
                    {hasSign
                      ? <span className="text-emerald-600 font-mono text-[10px]">✓ {d.esign_ack.customer_name?.slice(0, 15)}</span>
                      : <Button size="sm" variant="ghost" className="h-6 text-[10px] gap-1"
                          onClick={() => { setEsignOpen(d); setEsignForm({ customer_name: b?.customer?.first_name + " " + (b?.customer?.last_name || ""), aadhaar_last4: "" }); }}
                          data-testid={`esign-${d.delivery_number}`}>
                          <FileText className="w-3 h-3" /> eSign
                        </Button>}
                  </td>
                  <td className="font-mono text-xs">{d.gate_pass_number || "—"}</td>
                  <td className="text-xs" onClick={(e) => e.stopPropagation()}>
                    {otp.verified
                      ? <span className="text-emerald-700 font-mono text-[10px]">✓ EXITED</span>
                      : otp.otp && !otp.verified
                        ? <span className="font-mono text-[10px] text-purple-600">OTP sent</span>
                        : d.status === "delivered" && hasSign
                          ? <Button size="sm" variant="ghost" className="h-6 text-[10px] gap-1" disabled={otpBusy===d.id} onClick={() => genOtp(d)}
                              data-testid={`gen-otp-${d.delivery_number}`}>Gen OTP</Button>
                          : "—"}
                  </td>
                  <td className="text-right" onClick={(e) => e.stopPropagation()}>
                    {d.status === "scheduled" && hasSign && (
                      <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-emerald-700" onClick={() => issue(d)} data-testid={`issue-gp-${d.delivery_number}`}>
                        <ShieldCheck className="w-3 h-3" /> Issue Gate Pass
                      </Button>
                    )}
                    {d.status === "scheduled" && !hasSign && (
                      <span className="text-[10px] font-mono text-amber-600" title="Customer must eSign first">⚠ eSign first</span>
                    )}
                    {otp.otp && !otp.verified && (
                      <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-purple-700"
                        onClick={() => setOtpTarget(d)} data-testid={`verify-otp-${d.delivery_number}`}>
                        Verify OTP
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* eSign sheet */}
      <Sheet open={!!esignOpen} onOpenChange={(v) => !v && setEsignOpen(null)}>
        <SheetContent side="right" className="w-full sm:max-w-md rounded-none space-y-3">
          <SheetHeader>
            <SheetTitle className="font-heading">Customer eSign Acknowledgement</SheetTitle>
            <SheetDescription>Mock Aadhaar eSign — customer types their full name + last 4 digits of Aadhaar to confirm they received the vehicle, all documents, and accessories in OK condition.</SheetDescription>
          </SheetHeader>
          <div className="space-y-3">
            <div><Label>Full name (as on Aadhaar) *</Label>
              <Input value={esignForm.customer_name} onChange={(e) => setEsignForm({ ...esignForm, customer_name: e.target.value })}
                className="rounded-sm mt-1" data-testid="esign-name" />
            </div>
            <div><Label>Aadhaar last 4 digits *</Label>
              <Input value={esignForm.aadhaar_last4} maxLength={4}
                onChange={(e) => setEsignForm({ ...esignForm, aadhaar_last4: e.target.value.replace(/\D/g, "") })}
                className="rounded-sm mt-1 font-mono" data-testid="esign-aadhaar" placeholder="****" />
            </div>
            <div className="border rounded-sm p-2 bg-amber-50/30 text-[11px] text-amber-900">
              By signing, the customer acknowledges receipt of the vehicle, all documents, accessories, and confirms it is in acceptable condition. This unlocks the gate-pass step.
            </div>
            <Button onClick={saveEsign} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="esign-save">
              Save eSign
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Security OTP verify sheet */}
      <Sheet open={!!otpTarget} onOpenChange={(v) => !v && setOtpTarget(null)}>
        <SheetContent side="right" className="w-full sm:max-w-md rounded-none space-y-3">
          <SheetHeader>
            <SheetTitle className="font-heading">Security Gate — Verify Exit OTP</SheetTitle>
            <SheetDescription>Security guard verifies the OTP with the customer before letting the vehicle leave the premises.</SheetDescription>
          </SheetHeader>
          <div className="space-y-3">
            <div><Label>Enter OTP (6 digits)</Label>
              <Input value={otpVal} maxLength={6} onChange={(e) => setOtpVal(e.target.value.replace(/\D/g, ""))}
                className="rounded-sm mt-1 font-mono text-lg tracking-widest text-center" data-testid="otp-verify-input" placeholder="******" />
            </div>
            <Button onClick={verifyOtp} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="otp-verify-btn">
              Verify & Release Vehicle
            </Button>
          </div>
        </SheetContent>
      </Sheet>
      <RowDetailSheet row={detail} onClose={() => setDetail(null)} title="Delivery" testId="delivery-detail" />
    </div>
  );
}

// Iter-149 · Phase 3 · Cashier Desk — pending Uncollected Receipt Vouchers
function CashDeskTab() {
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("pending");
  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get("/dealership/uncollected-receipts");
      setRows(r.data || []);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const collect = async (row) => {
    const ref = window.prompt(`Cash / cheque reference for ${row.booking_number} (${fmtINR(row.amount)}):`, row.reference || "");
    if (ref === null) return;
    try {
      await api.post(`/dealership/uncollected-receipts/${row.id}/collect`, { reference: ref });
      toast.success(`₹${row.amount.toLocaleString("en-IN")} collected · receipt posted to GL`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const pending = rows.filter((r) => r.status === "pending_collection");
  const collected = rows.filter((r) => r.status === "collected");
  const visible = tab === "pending" ? pending : collected;

  return (
    <div className="space-y-3" data-testid="cash-desk-tab">
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent">Cash Desk · Receipts</div>
          <div className="text-[11px] text-muted-foreground">Cash / cheque / DD receipts. Pending ones wait for cashier confirmation. Collected ones are posted to the GL.</div>
        </div>
        <Button size="sm" variant="ghost" onClick={load} className="rounded-sm h-8 gap-1">
          <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>
      <div className="inline-flex gap-1 rounded-sm bg-muted p-1">
        <button onClick={() => setTab("pending")} className={`text-xs font-mono uppercase px-3 py-1 rounded-sm ${tab==="pending"?"bg-background shadow-sm":""}`} data-testid="cd-tab-pending">Pending ({pending.length})</button>
        <button onClick={() => setTab("collected")} className={`text-xs font-mono uppercase px-3 py-1 rounded-sm ${tab==="collected"?"bg-background shadow-sm":""}`} data-testid="cd-tab-collected">Collected ({collected.length})</button>
      </div>
      <div className="card-sharp overflow-hidden">
        <table className="w-full dense">
          <thead><tr><th>Booking#</th><th>Customer</th><th>Mode</th><th className="text-right">Amount</th><th>Reference</th><th>Since</th><th></th></tr></thead>
          <tbody>
            {visible.length === 0 && <tr><td colSpan="7" className="text-center py-10 text-muted-foreground">No {tab} receipts. All caught up ✓</td></tr>}
            {visible.map((r) => (
              <tr key={r.id} data-testid={`urv-row-${r.booking_number}`}
                    className="cursor-pointer hover:bg-muted/40 transition-colors"
                    onClick={() => setDetail(r)}>
                <td className="font-mono text-xs">{r.booking_number}</td>
                <td className="text-xs">{r.customer?.first_name} {r.customer?.last_name}</td>
                <td className="text-[10px] font-mono uppercase">{r.mode}</td>
                <td className="text-right tabular-nums font-medium">{fmtINR(r.amount)}</td>
                <td className="font-mono text-[10px] text-muted-foreground">{r.reference || "—"}</td>
                <td className="text-[10px] text-muted-foreground">{fmtDate(r.created_at)}</td>
                <td className="text-right" onClick={(e) => e.stopPropagation()}>
                  {r.status === "pending_collection" ? (
                    <Button size="sm" onClick={() => collect(r)} className="h-7 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white text-[10px]" data-testid={`urv-collect-${r.booking_number}`}>
                      ✓ Collected
                    </Button>
                  ) : (
                    <span className="text-[10px] font-mono uppercase text-emerald-600">✓ collected</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <RowDetailSheet row={detail} onClose={() => setDetail(null)} title="Cash Desk Receipt" testId="cash-desk-detail" />
    </div>
  );
}

// ========== Masters Tab (Vehicle Models + Accessories) ==========
function MastersTab() {
  const [models, setModels] = useState([]);
  const [acc, setAcc] = useState([]);
  const [tab, setTab] = useState("models");

  useEffect(() => {
    api.get("/dealership/vehicle-models").then((r) => setModels(r.data));
    api.get("/dealership/accessories").then((r) => setAcc(r.data));
  }, []);

  return (
    <div className="space-y-4" data-testid="masters-tab">
      <div className="flex gap-2 border-b flex-wrap">
        <button onClick={() => setTab("models")} className={`px-3 py-2 text-xs font-mono uppercase tracking-wider ${tab === "models" ? "border-b-2 border-accent text-foreground" : "text-muted-foreground"}`} data-testid="masters-models-tab">Vehicle Models</button>
        <button onClick={() => setTab("acc")} className={`px-3 py-2 text-xs font-mono uppercase tracking-wider ${tab === "acc" ? "border-b-2 border-accent text-foreground" : "text-muted-foreground"}`} data-testid="masters-acc-tab">Accessories</button>
        <button onClick={() => setTab("ew")} className={`px-3 py-2 text-xs font-mono uppercase tracking-wider ${tab === "ew" ? "border-b-2 border-accent text-foreground" : "text-muted-foreground"}`} data-testid="masters-ew-tab">Extended Warranty</button>
        <button onClick={() => setTab("bulk")} className={`px-3 py-2 text-xs font-mono uppercase tracking-wider ${tab === "bulk" ? "border-b-2 border-accent text-foreground" : "text-muted-foreground"}`} data-testid="masters-bulk-tab">Bulk Upload</button>
        <button onClick={() => setTab("config")} className={`px-3 py-2 text-xs font-mono uppercase tracking-wider ${tab === "config" ? "border-b-2 border-accent text-foreground" : "text-muted-foreground"}`} data-testid="masters-config-tab">Business Rules</button>
        <button onClick={() => setTab("toggles")} className={`px-3 py-2 text-xs font-mono uppercase tracking-wider ${tab === "toggles" ? "border-b-2 border-accent text-foreground" : "text-muted-foreground"}`} data-testid="masters-toggles-tab">Feature Toggles</button>
      </div>

      {tab === "models" && (
        <div className="card-sharp overflow-hidden">
          <table className="w-full dense">
            <thead><tr><th>Model</th><th>Variant</th><th>Fuel</th><th>Trans</th><th className="text-right">Ex-Showroom</th><th className="text-right">GST</th><th>Colours</th></tr></thead>
            <tbody>
              {models.length === 0 && <tr><td colSpan="7" className="text-center py-10 text-muted-foreground">No models yet.</td></tr>}
              {models.map((m) => (
                <tr key={m.id} data-testid={`model-row-${m.name}-${m.variant}`}>
                  <td className="text-xs font-medium">{m.name}</td>
                  <td className="text-xs">{m.variant}</td>
                  <td className="text-[10px] font-mono uppercase text-muted-foreground">{m.fuel_type}</td>
                  <td className="text-[10px] font-mono uppercase text-muted-foreground">{m.transmission}</td>
                  <td className="text-right tabular-nums font-medium">{fmtINR(m.ex_showroom_price)}</td>
                  <td className="text-right tabular-nums text-xs">{m.gst_rate}%</td>
                  <td className="text-[10px] text-muted-foreground">{(m.colours || []).join(", ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "acc" && (
        <div className="card-sharp overflow-hidden">
          <table className="w-full dense">
            <thead><tr><th>Name</th><th>Category</th><th className="text-right">Price (incl.)</th><th className="text-right">GST</th></tr></thead>
            <tbody>
              {acc.length === 0 && <tr><td colSpan="4" className="text-center py-10 text-muted-foreground">No accessories.</td></tr>}
              {acc.map((a) => (
                <tr key={a.id} data-testid={`acc-row-${a.name}`}>
                  <td className="text-xs font-medium">{a.name}</td>
                  <td className="text-[10px] font-mono uppercase text-muted-foreground">{a.category}</td>
                  <td className="text-right tabular-nums">{fmtINR(a.unit_price)}</td>
                  <td className="text-right tabular-nums text-xs">{a.gst_rate}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "ew" && <ExtendedWarrantyPanel />}
      {tab === "bulk" && <BulkUploadPanel />}
      {tab === "config" && <DealerConfigPanel />}
      {tab === "toggles" && <FeatureTogglesPanel />}
    </div>
  );
}

// ========== Main page ==========
export default function Dealership() {
  // Iter-153 · Load feature toggles so the tab list can hide sub-modules the
  // company doesn't use. Fall back to "everything on" if the endpoint 404s
  // (older orgs before feature_toggles landed).
  const [toggles, setToggles] = useState({});
  useEffect(() => {
    api.get("/industry-features")
      .then((r) => setToggles(r.data?.feature_toggles || {}))
      .catch(() => setToggles({}));
  }, []);
  const on = (key) => toggles[key] !== false;    // default ON when unset

  // Iter-154 · Deep-linking from sidebar (Dealership sub-modules) — sidebar
  // navigates with ?tab=<value>. We keep the URL and the active tab in sync so
  // clicking a sidebar item jumps straight to that tab, and clicking a tab
  // updates the URL for shareable bookmarks.
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(requested || "dashboard");
  useEffect(() => {
    if (requested && requested !== activeTab) setActiveTab(requested);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requested]);
  const onTabChange = (v) => {
    setActiveTab(v);
    const next = new URLSearchParams(searchParams);
    if (v === "dashboard") next.delete("tab"); else next.set("tab", v);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="space-y-4" data-testid="dealership-page">
      <PageHeader
        eyebrow="dealership 360"
        title="Automotive Dealership"
        description="Lead → Quote → Booking → Delivery. One funnel, all statutory math, live inventory."
      />
      <Tabs value={activeTab} onValueChange={onTabChange}>
        <TabsList className="rounded-sm flex-wrap h-auto">
          <TabsTrigger value="dashboard" className="rounded-sm" data-testid="tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="vehicle-360" className="rounded-sm" data-testid="tab-vehicle-360">Vehicle 360</TabsTrigger>
          <TabsTrigger value="leads" className="rounded-sm" data-testid="tab-leads">Leads</TabsTrigger>
          <TabsTrigger value="quotations" className="rounded-sm" data-testid="tab-quotations">Quotations</TabsTrigger>
          <TabsTrigger value="bookings" className="rounded-sm" data-testid="tab-bookings">Bookings</TabsTrigger>
          {on("cancellations") && <TabsTrigger value="cancellations" className="rounded-sm" data-testid="tab-cancellations">Cancellations</TabsTrigger>}
          {on("finance") && <TabsTrigger value="finance" className="rounded-sm" data-testid="tab-finance">Finance</TabsTrigger>}
          {on("insurance") && <TabsTrigger value="insurance" className="rounded-sm" data-testid="tab-insurance">Insurance</TabsTrigger>}
          {on("rto") && <TabsTrigger value="rto" className="rounded-sm" data-testid="tab-rto">RTO</TabsTrigger>}
          {on("discount_approvals") && <TabsTrigger value="approvals" className="rounded-sm" data-testid="tab-approvals">Approvals</TabsTrigger>}
          <TabsTrigger value="stock" className="rounded-sm" data-testid="tab-stock">Vehicle Stock</TabsTrigger>
          {on("price_lists") && <TabsTrigger value="price-lists" className="rounded-sm" data-testid="tab-price-lists">Price Lists</TabsTrigger>}
          {on("waitlist") && <TabsTrigger value="waitlist" className="rounded-sm" data-testid="tab-waitlist">Waitlist</TabsTrigger>}
          {on("deliveries") && <TabsTrigger value="deliveries" className="rounded-sm" data-testid="tab-deliveries">Deliveries</TabsTrigger>}
          {on("cash_desk") && <TabsTrigger value="cash-desk" className="rounded-sm" data-testid="tab-cash-desk">Cash Desk</TabsTrigger>}
          {on("service_desk") && <TabsTrigger value="service-desk" className="rounded-sm" data-testid="tab-service-desk">Service Desk</TabsTrigger>}
          <TabsTrigger value="masters" className="rounded-sm" data-testid="tab-masters">Masters</TabsTrigger>
          <TabsTrigger value="config-lists" className="rounded-sm" data-testid="tab-config-lists">Config Lists</TabsTrigger>
        </TabsList>
        <TabsContent value="dashboard" className="mt-4"><DashboardTab /></TabsContent>
        <TabsContent value="vehicle-360" className="mt-4"><VehicleLifecycleTab /></TabsContent>
        <TabsContent value="leads" className="mt-4"><LeadsTab /></TabsContent>
        <TabsContent value="quotations" className="mt-4"><QuotationsTab /></TabsContent>
        <TabsContent value="bookings" className="mt-4"><BookingsTab /></TabsContent>
        {on("cancellations") && <TabsContent value="cancellations" className="mt-4"><CancellationsTab /></TabsContent>}
        {on("finance") && <TabsContent value="finance" className="mt-4"><FinanceTab /></TabsContent>}
        {on("insurance") && <TabsContent value="insurance" className="mt-4"><InsuranceTab /></TabsContent>}
        {on("rto") && <TabsContent value="rto" className="mt-4"><RTOTab /></TabsContent>}
        {on("discount_approvals") && <TabsContent value="approvals" className="mt-4"><DiscountApprovalsTab /></TabsContent>}
        <TabsContent value="stock" className="mt-4"><StockTab /></TabsContent>
        {on("price_lists") && <TabsContent value="price-lists" className="mt-4"><PriceListsTab /></TabsContent>}
        {on("waitlist") && <TabsContent value="waitlist" className="mt-4"><WaitlistTab /></TabsContent>}
        {on("deliveries") && <TabsContent value="deliveries" className="mt-4"><DeliveriesTab /></TabsContent>}
        {on("cash_desk") && <TabsContent value="cash-desk" className="mt-4"><CashDeskTab /></TabsContent>}
        {on("service_desk") && <TabsContent value="service-desk" className="mt-4"><ServiceDeskTab /></TabsContent>}
        <TabsContent value="masters" className="mt-4"><MastersTab /></TabsContent>
        <TabsContent value="config-lists" className="mt-4"><MastersConfigurableLists /></TabsContent>
      </Tabs>
    </div>
  );
}
